import axios from 'axios';
import { get, post, put, API_URL } from '../utils/api';

// Configure API URL from environment or use default
// const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

// Constants for token handling
// We no longer store tokens in localStorage, they'll be in HttpOnly cookies
// maintained by the server and sent automatically with requests
const USER_KEY = 'user_info';

/**
 * Get available roles for user registration
 * @returns {Promise<Array>} List of available roles with value and displayName
 */
export const getRegistrationRoles = async () => {
  try {
    const response = await get(`${API_URL}/auth/registration-roles`);
    return response.data;
  } catch (error) {
    // console.error('Error fetching registration roles:', error.response?.data || error.message);
    throw error;
  }
};

/**
 * Authenticates a user with the server API
 * @param {string} email - User's email
 * @param {string} password - User's password
 * @returns {Promise<object>} - The authentication response with user info
 */
export const login = async (email, password) => {
  try {
    // console.log('authService: Attempting login...');
    
    // Send credentials to server - server will set HttpOnly cookies for tokens
    const response = await axios.post(`${API_URL}/auth/login`, 
      { email, password },
      { withCredentials: true } // Important: This enables cookies to be sent/received
    );
    
    // console.log('authService: Login response received:', response.data);
    
    // Return the entire structured response data from the server
    // The AuthContext will handle processing based on the status
    return response.data; 
    
  } catch (error) {
    // console.error('authService: Login error:', error);
    // Re-throw the error so AuthContext can handle it, potentially extracting details
    // from error.response.data if the server provides structured error responses
    if (error.response && error.response.data) {
      // Throw a new error that includes the structured data if available
      // This helps AuthContext get the specific message ('Invalid credentials.', etc.)
      const structuredError = new Error(error.response.data.message || error.message);
      structuredError.response = error.response; // Keep original response attached
      structuredError.data = error.response.data; // Attach structured data
      throw structuredError;
    } else {
      // Throw the original error if no structured response data
      throw error;
    }
  }
};

/**
 * Registers a new user with the server API
 * @param {object} userData - User registration data
 * @returns {Promise<object>} - The registration response
 */
export const register = async (userData) => {
  try {
    // console.log('authService: Attempting registration...');
    // Remove confirmPassword if present before sending
    const { confirmPassword, ...apiData } = userData;
    const response = await axios.post(`${API_URL}/auth/register`, apiData, {
      withCredentials: true // Important: This enables cookies to be sent/received
    });
    
    // console.log('authService: Registration response:', response.data);
    
    // The server now returns the user profile directly
    const newUserData = response.data;
    
    // Store user profile for UI purposes only
    if (newUserData) {
      localStorage.setItem(USER_KEY, JSON.stringify(newUserData));
    }
    
    return newUserData;
  } catch (error) {
    // console.error('authService: Registration error:', error);
    // Re-throw the error so the component can catch it and display a message
    // Include response data if available for more detailed error handling
    const errorToThrow = new Error(error.response?.data?.message || error.message);
    errorToThrow.response = error.response; // Attach the full response
    throw errorToThrow;
  }
};

/**
 * Register an internal user (Operator/Manager)
 * @param {object} userData - User registration data
 * @returns {Promise<object>} User data (no tokens expected for this flow?)
 */
export const registerInternalUser = async (userData) => {
  try {
    // console.log('authService: Attempting INTERNAL registration...');
    const { confirmPassword, ...apiData } = userData;
    // Call the new internal registration endpoint using axios for consistency
    const response = await axios.post(`${API_URL}/auth/register-internal`, apiData, {
      // Include credentials if this route expects cookies (e.g., for CSRF), 
      // otherwise remove if it's truly public/unauthenticated
      withCredentials: true 
    });
    // console.log('authService: Internal registration successful', response.data);
    // This endpoint likely just returns the user info, no tokens to store
    return response.data; 
  } catch (error) {
    // console.error('authService: Internal registration error:', error);
    const errorToThrow = new Error(error.response?.data?.message || error.message);
    errorToThrow.response = error.response; 
    throw errorToThrow;
  }
};

/**
 * Logs out the current user
 */
export const logout = async () => {
  try {
    // console.log('authService: Starting logout process');
    
    // Call the server to invalidate cookies and perform server-side logout
    await axios.post(
      `${API_URL}/auth/logout`,
      {},
      { withCredentials: true }
    );
    
    // Clear local user data
    localStorage.removeItem(USER_KEY);
    
    // console.log('authService: Logout completed successfully');
  } catch (error) {
    // console.error('authService: Logout error:', error);
    // Still clear local user data even if the server request fails
    localStorage.removeItem(USER_KEY);
  }
};

/**
 * Gets the current user's profile from the server
 * @returns {Promise<object|null>} - User profile or null if not authenticated
 */
export const getUserProfile = async () => {
  try {
    // Server will check the cookie-based token
    const response = await get(`${API_URL}/auth/me`, { withCredentials: true });
    
    if (!response.data) {
      // console.error('Empty response data from profile endpoint');
      return null;
    }
    
    // The server now returns the user profile data directly
    const userData = response.data;
    
    // Store user info locally for UI purposes only
    localStorage.setItem(USER_KEY, JSON.stringify(userData));
    
    return userData;
  } catch (error) {
    // console.error('Error fetching user profile:', error);
    return null;
  }
};

/**
 * Gets the locally stored user information
 * This is only for UI/UX purposes - all auth decisions should be made server-side
 * @returns {object|null} - User information or null
 */
export const getStoredUserInfo = () => {
  try {
    const userInfo = localStorage.getItem(USER_KEY);
    return userInfo ? JSON.parse(userInfo) : null;
  } catch (e) {
    // console.error('Error reading user info from localStorage:', e);
    localStorage.removeItem(USER_KEY); // Clear invalid data
    return null;
  }
};

/**
 * Sets the user information in local storage.
 * Used by AuthContext after successful login.
 * @param {object} userInfo - The user object to store.
 */
export const setStoredUserInfo = (userInfo) => {
  try {
    if (userInfo) {
      localStorage.setItem(USER_KEY, JSON.stringify(userInfo));
    } else {
      localStorage.removeItem(USER_KEY);
    }
  } catch (e) {
    // console.error('Error writing user info to localStorage:', e);
  }
};

/**
 * Updates the current user's profile
 * @param {object} profileData - The profile data to update
 * @returns {Promise<object>} - Updated user profile
 */
export const updateUserProfile = async (profileData) => {
  try {
    // console.log('authService: Attempting profile update...');
    
    const response = await put(`${API_URL}/auth/me`, profileData, {
      withCredentials: true
    });
    
    // console.log('authService: Profile update successful:', response.data);
    
    // Update local storage with new profile data
    if (response.data) {
      localStorage.setItem(USER_KEY, JSON.stringify(response.data));
    }
    
    return response.data;
  } catch (error) {
    // console.error('authService: Profile update error:', error);
    const errorToThrow = new Error(error.response?.data?.message || error.message);
    errorToThrow.response = error.response;
    throw errorToThrow;
  }
};

/**
 * Changes the current user's password
 * @param {string} currentPassword - Current password
 * @param {string} newPassword - New password
 * @returns {Promise<object>} - Password change confirmation
 */
export const changePassword = async (currentPassword, newPassword) => {
  try {
    // console.log('authService: Attempting password change...');
    
    const response = await post(`${API_URL}/auth/password`, {
      currentPassword,
      newPassword
    }, {
      withCredentials: true
    });
    
    // console.log('authService: Password change successful');
    return response.data;
  } catch (error) {
    // console.error('authService: Password change error:', error);
    const errorToThrow = new Error(error.response?.data?.message || error.message);
    errorToThrow.response = error.response;
    throw errorToThrow;
  }
};

/**
 * Determines if a user is authenticated based on local state
 * Note: This only checks the local state for UI purposes.
 * All protected API calls will verify authentication server-side via cookie
 * @returns {boolean} - Whether the user appears to be authenticated
 */
export const isAuthenticated = () => {
  try {
    // First check if we have user data in localStorage
    const user = JSON.parse(localStorage.getItem(USER_KEY));
    
    // If we have user data, we consider the user authenticated for UI purposes
    // (The server will verify actual authentication via cookies for API requests)
    return !!user && user.email;
  } catch (error) {
    // console.error('authService: Error in isAuthenticated:', error);
    return false;
  }
};

/**
 * Checks if user is LIKELY authenticated based on local data
 * This is for UI purposes only and should not be used for security decisions
 * @returns {boolean} - True if user info exists locally
 */
export const hasLocalUserInfo = () => {
  return !!localStorage.getItem(USER_KEY);
};

/**
 * Refreshes the access token using the refresh token
 * @returns {Promise<boolean>} True if refresh was successful
 */
export const refreshAccessToken = async () => {
  try {
    // console.log('authService: Attempting to refresh access token');
    const response = await axios.post(`${API_URL}/auth/token/refresh`, {}, {
      withCredentials: true // Important: This enables cookies to be sent/received
    });
    
    // console.log('authService: Token refresh successful');
    return true;
  } catch (error) {
    // console.error('authService: Token refresh failed:', error);
    return false;
  }
};

/**
 * Sets up axios interceptors for handling authentication errors
 * @param {Function} onLogout - Function to call when logout is needed
 * @returns {Function} - Function to remove the interceptors
 */
export const setupAxiosInterceptors = (onLogout) => {
  let responseInterceptor = null;
  let isRefreshing = false;
  let failedQueue = [];
  
  const processQueue = (error, token = null) => {
    failedQueue.forEach(prom => {
      if (error) {
        prom.reject(error);
      } else {
        prom.resolve(token);
      }
    });
    
    failedQueue = [];
  };
  
  try {
    // Response interceptor for handling 401 errors (unauthenticated)
    responseInterceptor = axios.interceptors.response.use(
      (response) => {
        return response;
      },
      async (error) => {
        // Handle 401 Unauthorized or 403 Forbidden errors
        if (error.response && (error.response.status === 401 || error.response.status === 403)) {
          const originalRequest = error.config;
          
          // Prevent infinite loops - don't retry refresh token endpoints or already retried requests
          if (
            originalRequest.url.includes('/auth/token/refresh') || 
            originalRequest._retry
          ) {
            // If it's already a refresh token request that failed, we need to log out
            // console.log('Token refresh failed or max retries reached, logging out');
            
            // Clear local user data
            localStorage.removeItem(USER_KEY);
            
            // Call the logout callback if provided
            if (onLogout) {
              onLogout(false); // Don't try to refresh, just log out
            }
            
            return Promise.reject(error);
          }
          
          if (isRefreshing) {
            // If we're already refreshing, add this request to the queue
            return new Promise((resolve, reject) => {
              failedQueue.push({ resolve, reject });
            })
              .then(() => {
                return axios(originalRequest);
              })
              .catch(err => {
                return Promise.reject(err);
              });
          }
          
          // Mark this request as retried to prevent infinite loops
          originalRequest._retry = true;
          isRefreshing = true;
          
          try {
            // Call the logout callback with a flag to try refreshing first
            if (onLogout) {
              const refreshSuccess = await onLogout(true); // Try to refresh
              
              if (refreshSuccess) {
                // Token refreshed successfully via the callback
                processQueue(null);
                isRefreshing = false;
                return axios(originalRequest);
              } else {
                // Token refresh failed
                processQueue(error);
                isRefreshing = false;
                return Promise.reject(error);
              }
            } else {
              // No logout callback provided, try to refresh locally
              const refreshSuccess = await refreshAccessToken();
              
              if (refreshSuccess) {
                // Token refreshed successfully, retry the original request
                processQueue(null);
                isRefreshing = false;
                return axios(originalRequest);
              } else {
                // Token refresh failed
                processQueue(error);
                isRefreshing = false;
                
                // Clear local user data
                localStorage.removeItem(USER_KEY);
                return Promise.reject(error);
              }
            }
          } catch (refreshError) {
            processQueue(refreshError);
            isRefreshing = false;
            
            // Clear local user data
            localStorage.removeItem(USER_KEY);
            return Promise.reject(refreshError);
          }
        }
        
        return Promise.reject(error);
      }
    );
    
    // Return a function to remove the interceptors
    return () => {
      axios.interceptors.response.eject(responseInterceptor);
    };
  } catch (error) {
    // console.error('Error setting up axios interceptors:', error);
    return () => {}; // Return empty function in case of error
  }
}; 